import React, { useState, useEffect } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import "../modal/modal.scss";
import TableContainer from "@material-ui/core/TableContainer";
import {
  Table,
  TableRow,
  TableHead,
  TableCell,
  TableBody,
  withStyles,
} from "@material-ui/core";
import TablePagination from "@material-ui/core/TablePagination";
import Loader from "../Loader/Loader";
import Tablestyles from "../../screens/Mapping/ROCITable.module.css";
import * as XLSX from "@lokalise/xlsx";

const StyledTableCell = withStyles((theme) => ({
  head: {
    backgroundColor: "#e5e5e5",
    color: "#212529",
    height: 3,
    width: "auto", // let content decide
    minWidth: "50px",
    maxWidth: "250px",
    whiteSpace: "normal",
  },
  body: {
    fontSize: 12,
    fontFamily: "Roboto,Helvetica,Arial,sans-serif",
    width: "auto", // let content decide
    minWidth: "50px",
    maxWidth: "250px",
    whiteSpace: "normal",
  },
  root: {
    padding: "15px",
  },
}))(TableCell);

const Row = ({ keys, rowData, headingRow }) => {
  let data = rowData;

  const formatArrayForUI = (arr) => {
    if (arr.length === 0) return "";
    return arr
      .map(
        (obj, index) =>
          `${index + 1}. ` +
          Object.entries(obj)
            .map(([key, value]) => `${key}: ${value}`)
            .join(" | ")
      )
      .join("\n");
  };

  const isLongValue = (value) => {
    if (value === null || value === "null") {
      return false;
    }

    // String or Array → check length
    if (typeof value === "string") {
      return value.length > 40;
    }

    // Object → check number of keys
    if (typeof value === "object") {
      return Object.keys(value).length > 0 && JSON.stringify(value).length > 40;
    }

    if (typeof value === "array") {
      const Stringed_Array = formatArrayForUI(value);
      return Stringed_Array && Stringed_Array.length > 30;
    }

    return false;
  };

  return (
    <TableRow
      key={`Rowkey-${keys}`}
      className={"table-row"}
      style={{ cursor: "pointer" }}
    >
      {headingRow.map((item, index) => (
        <StyledTableCell
          key={`key-${index}`}
          className={""}
          style={{ cursor: "pointer" }}
        >
          <div
            className="scrollable-cell cell-wrapper"
            style={{
              position: "relative",
              height: "40px",
              padding: "2px 3px",
              border: isLongValue(data?.[item.name])
                ? "1px solid #e0e0e0"
                : "none",
              overflowY: isLongValue(data?.[item.name]) ? "scroll" : "visible",
              whiteSpace: "nowrap",
              display: "block",
              textWrap: "wrap",
            }}
          >
            {data[item.name] && typeof data[item.name] === "object"
              ? JSON.stringify(data[item.name])
              : Array.isArray(data[item.name])
              ? JSON.stringify(data[item.name], null, 2)
              : data[item.name] || "None"}
          </div>
        </StyledTableCell>
      ))}
    </TableRow>
  );
};

export default function HistoryAndLogsModal(props) {
  const {
    sourceData,
    openModal,
    closeModel,
    isTableLoading,
    tableData,
    historyAndLogsTableHeaders,
    updateAlert,
  } = props;
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [paginatedRows, setPaginatedRows] = useState([]);

  useEffect(() => {
    setOpen(openModal);
  }, [openModal]);

  useEffect(() => {
    setPaginatedRows(tableData.slice(0, rowsPerPage));
  }, [tableData]);

  const handleClose = () => {
    setOpen(false);
    closeModel();
  };

  const handlePaginatedRows = (newPage) => {
    const start = newPage * rowsPerPage;
    const end = start + rowsPerPage;
    const currentPageRows = tableData.slice(start, end);
    return currentPageRows;
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    const Paginated_Rows = handlePaginatedRows(newPage);
    setPaginatedRows(Paginated_Rows);
  };

  const handleChangeRowsPerPage = (e) => {
    const value = parseInt(e.target.value);
    setRowsPerPage(value);
    const start = 0 * value;
    const end = start + value;
    const currentPageRows = tableData.slice(start, end);
    setPaginatedRows(currentPageRows);
    setPage(0);
  };

  const downloadSample = (e) => {
    e.stopPropagation();

    if (!tableData.length) {
      updateAlert({
        open: true,
        message: "No sample data available to download.",
        severity: "Failed",
        closeFlag: true,
      });
      return;
    }

    const order_check_data = [
      ...(historyAndLogsTableHeaders.map((item) => item.name) || []),
    ];

    const Download_Data = tableData.map((item) => {
      let result = {};
      order_check_data.forEach((key) => {
        result = {
          ...result,
          [key]: item[key] || "",
        };
      });
      return result;
    });

    const sampleData = [...Download_Data];
    const worksheet = XLSX.utils.json_to_sheet(sampleData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sample");
    XLSX.writeFile(
      workbook,
      `${sourceData?.current_source || "SampleData"}_${
        Download_Data?.[0]?.HARBOR_SOURCE_FILE_ID || "Default"
      }.xlsx`
    );
  };

  const DownloadButtonStyle = {
    marginTop: "0px",
    display: "flex",
    alignItems: "center",
    fontSize: "14px",
    justifyContent: "center",
    gap: "5px",
    backgroundColor: "#46596A",
    padding: "6px 12px",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
  };

  return (
    <React.Fragment>
      <Dialog
        fullWidth={true}
        maxWidth={"xl"}
        open={open}
        onClose={handleClose}
      >
        <DialogTitle>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "10px",
            }}
          >
            <span>Table Records</span>
            {!isTableLoading && tableData && tableData.length > 0 && (
              <button onClick={downloadSample} style={DownloadButtonStyle}>
                Download Template
              </button>
            )}
          </div>
        </DialogTitle>
        <DialogContent dividers>
          <DialogContentText>
            {isTableLoading && <Loader />}
            {!isTableLoading && tableData && tableData.length > 0 && (
              <>
                <TableContainer className={Tablestyles.budget_tb3}>
                  <Table stickyHeader>
                    <TableHead className="table-heading">
                      <TableRow>
                        {historyAndLogsTableHeaders.map((item, i) => (
                          <StyledTableCell key={i}>
                            {item.label}
                          </StyledTableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedRows.map((row, index) => (
                        <Row
                          keys={index}
                          rowData={row}
                          headingRow={historyAndLogsTableHeaders || []}
                        />
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* PAGINATION */}
                {tableData.length > 0 && (
                  <div>
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25]}
                      component="div"
                      count={tableData.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                  </div>
                )}
              </>
            )}

            {!isTableLoading && !tableData.length && (
              <div style={{ textAlign: "center", marginTop: "10px" }}>
                No Data Found.
              </div>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}
